import { useCallback, useEffect, useState } from 'react';
import { Cake, Sparkles, X } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { payrollAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { dismissBirthdayForSession, isBirthdayDismissedForSession, normalizeOperationalDateIso, OPERATIONAL_TIMEZONE_LABEL } from '@/lib/operationalTime';

const EMPTY_BIRTHDAY_BANNER = {
  date: '',
  count: 0,
  messages: [],
  timezone: OPERATIONAL_TIMEZONE_LABEL,
};

let cachedBirthdayBanner = null;

export const GlobalBirthdayBanner = () => {
  const { user } = useAuth();
  const [birthdayBanner, setBirthdayBanner] = useState(EMPTY_BIRTHDAY_BANNER);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let ignore = false;

    if (!user) {
      setBirthdayBanner(EMPTY_BIRTHDAY_BANNER);
      return () => {
        ignore = true;
      };
    }

    const loadBirthdayBanner = async () => {
      if (cachedBirthdayBanner) {
        setBirthdayBanner(cachedBirthdayBanner);
        return;
      }

      try {
        const response = await payrollAPI.getTodayBirthdays();
        if (ignore) return;
        const payload = response?.data || response || EMPTY_BIRTHDAY_BANNER;
        const operationalDate = normalizeOperationalDateIso(payload.date);
        const nextBanner = {
          date: operationalDate,
          count: Number(payload.count || 0),
          messages: Array.isArray(payload.messages) ? payload.messages : [],
          timezone: payload.timezone?.label || OPERATIONAL_TIMEZONE_LABEL,
        };
        cachedBirthdayBanner = nextBanner;
        setBirthdayBanner(nextBanner);
      } catch {
        if (!ignore) {
          setBirthdayBanner(EMPTY_BIRTHDAY_BANNER);
        }
      }
    };

    void loadBirthdayBanner();
    return () => {
      ignore = true;
    };
  }, [user?.id]);

  useEffect(() => {
    setDismissed(isBirthdayDismissedForSession({ userId: user?.id, dateIso: birthdayBanner.date }));
  }, [birthdayBanner.date, user?.id]);

  const handleDismiss = useCallback(() => {
    dismissBirthdayForSession({ userId: user?.id, dateIso: birthdayBanner.date });
    setDismissed(true);
  }, [birthdayBanner.date, user?.id]);

  if (!birthdayBanner.messages.length || dismissed) {
    return null;
  }

  return (
    <div
      className="border-b border-amber-200 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.28),_transparent_42%),linear-gradient(135deg,_#fff7ed_0%,_#fef3c7_45%,_#fff7ed_100%)]"
      data-testid="global-birthday-banner"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 py-3 lg:px-6">
        <div className="mt-0.5 rounded-full bg-white/90 p-2 shadow-sm" data-testid="global-birthday-banner-icon">
          <Cake className="h-5 w-5 text-amber-600" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700" data-testid="global-birthday-banner-title">
                Parabéns do dia
              </p>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-white/80 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid="global-birthday-banner-count">
                <Sparkles className="h-3 w-3" />
                {birthdayBanner.count}
              </span>
              <span className="inline-flex items-center rounded-full border border-amber-200 bg-white/70 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid="global-birthday-banner-timezone">
                {birthdayBanner.timezone || OPERATIONAL_TIMEZONE_LABEL}
              </span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full border border-amber-200 bg-white/80 text-amber-700 hover:bg-white"
              onClick={handleDismiss}
              data-testid="global-birthday-banner-dismiss-button"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-1 space-y-1">
            {birthdayBanner.messages.map((item, index) => (
              <p
                key={`${item.employee_id || item.employee_number || 'birthday'}-${index}`}
                className="text-sm font-semibold leading-relaxed text-slate-900"
                data-testid={`global-birthday-banner-message-${index}`}
              >
                {item.message}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};